/**
 * protocolLoader.js
 *
 * Loads and caches triage protocol JSONs. Bundles them at build time via
 * static imports so there are no runtime fetch calls — all protocols ship
 * in the JS bundle. Each protocol is indexed by its `id` field and also by
 * every term in its `searchTerms` array for fast suggestion lookup.
 */

import fever from './protocols/fever.json';
import cough_uri from './protocols/cough_uri.json';
import sore_throat from './protocols/sore_throat.json';
import ear_pain from './protocols/ear_pain.json';
import vomiting_diarrhea from './protocols/vomiting_diarrhea.json';
import abdominal_pain from './protocols/abdominal_pain.json';
import urinary_symptoms from './protocols/urinary_symptoms.json';
import rash_skin from './protocols/rash_skin.json';
import headache from './protocols/headache.json';
import minor_injury_sprain from './protocols/minor_injury_sprain.json';
import chest_discomfort from './protocols/chest_discomfort.json';
import shortness_of_breath from './protocols/shortness_of_breath.json';
import dizziness from './protocols/dizziness.json';
import allergic_reaction from './protocols/allergic_reaction.json';
import constipation from './protocols/constipation.json';
import joint_pain_swelling from './protocols/joint_pain_swelling.json';
import vaginal_symptoms from './protocols/vaginal_symptoms.json';
import rectal_bleeding from './protocols/rectal_bleeding.json';
import palpitations from './protocols/palpitations.json';
import weakness_fatigue from './protocols/weakness_fatigue.json';

const ALL_PROTOCOLS = [
  fever,
  cough_uri,
  sore_throat,
  ear_pain,
  vomiting_diarrhea,
  abdominal_pain,
  urinary_symptoms,
  rash_skin,
  headache,
  minor_injury_sprain,
  chest_discomfort,
  shortness_of_breath,
  dizziness,
  allergic_reaction,
  constipation,
  joint_pain_swelling,
  vaginal_symptoms,
  rectal_bleeding,
  palpitations,
  weakness_fatigue,
];

// Index by id for O(1) lookup
const BY_ID = {};
ALL_PROTOCOLS.forEach(p => {
  BY_ID[p.id] = p;
});

/**
 * Get a protocol by its exact id.
 * @param {string} id
 * @returns {object|null}
 */
export function getProtocol(id) {
  return BY_ID[id] || null;
}

/**
 * Get all protocols.
 * @returns {object[]}
 */
export function getAllProtocols() {
  return ALL_PROTOCOLS;
}

/**
 * Search protocols by free-text query. Returns an ordered array of
 * { protocol, score } matches, best first. Used by the intake screen
 * to suggest protocols as the user types.
 *
 * Scoring:
 *   3 — label starts with query (case-insensitive)
 *   2 — any searchTerm starts with query
 *   1 — label or searchTerm contains query anywhere
 *
 * @param {string} query
 * @param {number} [limit=5]
 * @returns {{ protocol: object, score: number }[]}
 */
export function searchProtocols(query, limit = 5) {
  if (!query || query.trim().length < 2) return [];
  const q = query.trim().toLowerCase();

  const scored = [];
  for (const protocol of ALL_PROTOCOLS) {
    const label = protocol.label.toLowerCase();
    let score = 0;

    if (label.startsWith(q)) {
      score = 3;
    } else if ((protocol.searchTerms || []).some(t => t.toLowerCase().startsWith(q))) {
      score = 2;
    } else if (
      label.includes(q) ||
      (protocol.searchTerms || []).some(t => t.toLowerCase().includes(q))
    ) {
      score = 1;
    }

    if (score > 0) {
      scored.push({ protocol, score });
    }
  }

  return scored
    .sort((a, b) => b.score - a.score || a.protocol.label.localeCompare(b.protocol.label))
    .slice(0, limit);
}

/**
 * Get suggested protocols for the intake "Common right now" suggestions.
 * Returns a curated set of frequently relevant protocols.
 * @returns {object[]}
 */
export function getDefaultSuggestions() {
  return [
    BY_ID['fever'],
    BY_ID['cough_uri'],
    BY_ID['sore_throat'],
    BY_ID['headache'],
    BY_ID['abdominal_pain'],
    BY_ID['minor_injury_sprain'],
  ].filter(Boolean);
}
